// Studio Workspace B1 — a thin ticker-lines editor (add / reorder / remove /
// save). Reused for the show template (/creator/shows) and the in-show live
// ticker (HostControls). Persisting is the caller's onSave.

import { useState } from 'react';

export function TickerEditor({
  initial,
  onSave,
  saving = false,
}: {
  initial: string[];
  onSave: (lines: string[]) => void;
  saving?: boolean;
}) {
  const key = initial.join('');
  const [seedKey, setSeedKey] = useState(key);
  const [lines, setLines] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');

  // Reseed when the source content changes (different show / post-save refetch).
  if (key !== seedKey) {
    setSeedKey(key);
    setLines(initial);
  }

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    setLines((l) => [...l, v]);
    setDraft('');
  };
  const remove = (i: number) => setLines((l) => l.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) =>
    setLines((l) => {
      const j = i + dir;
      if (j < 0 || j >= l.length) return l;
      const next = [...l];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });

  return (
    <div className="space-y-2">
      <ul className="space-y-1">
        {lines.map((line, i) => (
          <li key={i} className="flex items-center gap-2 font-sans text-sm">
            <span className="min-w-0 flex-1 truncate text-video-text">{line}</span>
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              aria-label="Move up"
              className="px-1 text-video-text-faint hover:text-video-text disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === lines.length - 1}
              aria-label="Move down"
              className="px-1 text-video-text-faint hover:text-video-text disabled:opacity-30"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label="Remove line"
              className="px-1 text-video-text-faint hover:text-oxblood-live"
            >
              ✕
            </button>
          </li>
        ))}
        {lines.length === 0 && (
          <li className="font-sans text-xs text-video-text-faint">No ticker lines yet.</li>
        )}
      </ul>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add a ticker line"
          className="min-w-0 flex-1 rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          className="rounded-md border border-video-line px-3 py-2 font-sans text-xs text-video-text-muted hover:text-video-text disabled:opacity-50"
        >
          Add
        </button>
      </div>
      <button
        type="button"
        onClick={() => onSave(lines)}
        disabled={saving}
        className="rounded-md bg-video-text px-4 py-2 font-sans text-sm font-medium text-video-bg disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save ticker'}
      </button>
    </div>
  );
}
